import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Tabs from '../../components/ui/Tabs';
import DatePicker from '../../components/ui/DatePicker';
import { pushToast } from '../../components/ui/toastBus';
import { CandidateCell } from '../../components/requests/RequestActions';
import { StaffRadioList, fmtDateTime, nowLocalInput, toIso, toLocalInput } from '../../components/requests/AssignDeadlineFields';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import { listScheduledSteps, scheduledStepCounts, listAssignmentStaff, reassignStep } from '../../api/assignments';
import { ASSIGNED_JOBS_QUERY_KEY } from '../../hooks/useAssignedJobsCount';

const SCHEDULED_KEY = ['scheduled-steps'];

// mode 'reassign' changes the staff member (deadline optional); 'reschedule' changes only the deadline.
function ChangeModal({ assignment, mode, onClose, onDone }) {
  const queryClient = useQueryClient();
  const reassign = mode === 'reassign';
  const [assignee, setAssignee] = useState(assignment.assigned_to.id);
  const [deadline, setDeadline] = useState(toLocalInput(assignment.deadline));

  const staff = useQuery({
    queryKey: ['scheduled-steps', 'staff', assignment.id],
    queryFn: () => listAssignmentStaff(assignment.id),
    enabled: reassign,
  });

  const mutation = useMutation({
    mutationFn: () =>
      reassignStep(assignment.id, {
        assigned_to: reassign ? assignee : undefined,
        deadline: toIso(deadline),
      }),
    onSuccess: () => {
      pushToast({ type: 'success', message: reassign ? 'Step reassigned.' : 'Step rescheduled.' });
      queryClient.invalidateQueries({ queryKey: SCHEDULED_KEY });
      queryClient.invalidateQueries({ queryKey: ASSIGNED_JOBS_QUERY_KEY });
      onDone();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not update the assignment.' }),
  });

  const valid = deadline && new Date(deadline) > new Date();

  return (
    <Modal
      open
      onClose={onClose}
      title={`${reassign ? 'Reassign' : 'Reschedule'} — ${assignment.step.title}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!valid}>
            {reassign ? 'Reassign' : 'Reschedule'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {reassign && (
          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">Assign to</p>
            {staff.isLoading ? (
              <Spinner />
            ) : (
              <StaffRadioList staff={staff.data || []} value={assignee} onChange={setAssignee} emptyText="No eligible staff found." />
            )}
          </div>
        )}
        <DatePicker label="Deadline" type="datetime-local" min={nowLocalInput()} value={deadline} onChange={(e) => setDeadline(e.target.value)} />
      </div>
    </Modal>
  );
}

const baseColumns = [
  { key: 'candidate', label: 'Candidate', render: (row) => <CandidateCell candidate={row.candidate} /> },
  { key: 'step', label: 'Step', render: (row) => row.step.title },
  { key: 'assignee', label: 'Assigned to', render: (row) => row.assigned_to.name },
  {
    key: 'assigned_by',
    label: 'Assigned by',
    render: (row) => (
      <div>
        {row.assigned_by}
        <div className="text-xs text-gray-400">{fmtDateTime(row.assigned_at)}</div>
      </div>
    ),
  },
];

function columnsFor(tab, setChange) {
  if (tab === 'waiting') {
    return [
      ...baseColumns,
      { key: 'deadline', label: 'Deadline', render: (row) => fmtDateTime(row.deadline) },
      { key: 'status', label: 'Status', render: () => <Badge tone="amber">Waiting for approval</Badge> },
      {
        key: 'review',
        label: '',
        render: () => (
          <div className="flex justify-end">
            <Link to="/candidates/submission-requests" className="text-sm font-medium text-primary hover:underline">
              Review request
            </Link>
          </div>
        ),
      },
    ];
  }
  if (tab === 'completed') {
    return [
      ...baseColumns,
      { key: 'deadline', label: 'Deadline', render: (row) => fmtDateTime(row.deadline) },
      {
        key: 'completed',
        label: 'Completed',
        render: (row) => (
          <div>
            {fmtDateTime(row.completed_at)}
            {new Date(row.completed_at) > new Date(row.deadline) && <Badge tone="amber" className="ml-2">Late</Badge>}
          </div>
        ),
      },
    ];
  }
  return [
    ...baseColumns,
    {
      key: 'deadline',
      label: 'Deadline',
      render: (row) => (
        <div>
          {fmtDateTime(row.deadline)}
          {row.is_overdue && <Badge tone="red" className="ml-2">Overdue</Badge>}
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setChange({ assignment: row, mode: 'reassign' })}>
            Re-assign
          </Button>
          <Button variant="secondary" onClick={() => setChange({ assignment: row, mode: 'reschedule' })}>
            Reschedule
          </Button>
        </div>
      ),
    },
  ];
}

const EMPTY = {
  ongoing: 'No assigned steps are currently in progress.',
  overdue: 'Nothing is past its deadline.',
  waiting: 'No submitted steps are waiting for approval.',
  completed: 'No assigned steps have been completed yet.',
};

export default function ScheduledSteps() {
  const [tab, setTab] = useState('ongoing');
  const [change, setChange] = useState(null);

  const counts = useQuery({ queryKey: [...SCHEDULED_KEY, 'counts'], queryFn: scheduledStepCounts, refetchInterval: 60_000 });
  const filters = useCandidateFilters();
  const query = useQuery({ queryKey: [...SCHEDULED_KEY, tab, filters.params], queryFn: () => listScheduledSteps(tab, filters.params) });
  const rows = query.data || [];

  const tabs = [
    { value: 'ongoing', label: `Ongoing${counts.data ? ` (${counts.data.ongoing})` : ''}` },
    { value: 'overdue', label: `Overdue${counts.data ? ` (${counts.data.overdue})` : ''}` },
    { value: 'waiting', label: `Waiting for approval${counts.data ? ` (${counts.data.waiting})` : ''}` },
    { value: 'completed', label: `Completed${counts.data ? ` (${counts.data.completed})` : ''}` },
  ];

  return (
    <div>
      <PageHeader
        title="Scheduled Steps"
        description="Every step handed to a staff member with a deadline. Ongoing includes steps before and after their deadline; Overdue shows only those past it. Steps the staff member has submitted move to Waiting for approval until reviewed."
      />
      <div className="mb-4">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      <CandidateFilterBar {...filters.barProps} />
      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="Nothing here" message={EMPTY[tab]} />
      ) : (
        <Table columns={columnsFor(tab, setChange)} data={rows} />
      )}

      {change && (
        <ChangeModal key={`${change.assignment.id}-${change.mode}`} assignment={change.assignment} mode={change.mode} onClose={() => setChange(null)} onDone={() => setChange(null)} />
      )}
    </div>
  );
}
